import { Component, ViewChild, ElementRef, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as moment from 'moment';
import { Duration, Moment } from 'moment';

import { AuthService } from '../../services/auth/auth.service';
import { VideoPortalUser } from '../../services/auth/video-portal-user.model';
import { CameraService } from '../../services/camera/camera.service';
import { ConfigService } from '../../services/config/config.service';
import { DateService } from '../../services/date/date.service';
import { FlashMessagesService } from '../../services/flash-messages/flash-messages.service';
import { CreateVideoRequest } from '../../services/video-requests/create-video-request.model';
import { VideoRequestsService } from '../../services/video-requests/video-requests.service';
import { Business } from '../../services/trucks/truck.model';
import { ReturnUrlService } from '../../services/return-url/return-url.service';

declare var $: any;

@Component({
    selector: 'app-create-video-request',
    templateUrl: './create-video-request.component.html',
    styleUrls: ['./create-video-request.component.css']
})
export class CreateVideoRequestComponent {
    cameras: string[];
    computerType: string;
    deviceId: string;
    ipAddress: string;
    location: string;

    messageIds = {
        httpError: <string>null
    };
    oldest: Moment;
    reason: string;

    selectedCamerasMap: Map<string, boolean>;
    site: string;
    softwareVersion: number;
    timestamp: Moment;
    title: string;
    truckNumber: string;
    userInfo: VideoPortalUser;
    isFreeDownload: boolean;
    business: Business;
    selectedToHour: string;
    selectedToMinute: string;
    selectedFromHour: string;
    selectedFromMinute: string;
    selectedToMeridiem: string;
    selectedFromMeridiem: string;

    hours: string[] = [];
    minutes: string[] = [];
    meridiems: string[] = [];

    @ViewChild('freeDownloadDisclaimerModal') freeDownloadDisclaimerModal: ElementRef;

    // noinspection TsLint
    private _requestDate: Date;
    private _requestFromTime: Date;
    private _requestToTime: Date;

    constructor(private route: ActivatedRoute,
        private authService: AuthService,
        private cameraService: CameraService,
        public configService: ConfigService,
        private dateService: DateService,
        private flashMessagesService: FlashMessagesService,
        private router: Router,
        private returnUrlService: ReturnUrlService,
        private videoRequestService: VideoRequestsService) {

        this.userInfo = this.authService.userInfo;

        // TODO: Add guards for invalid params here.
        const snapshot = this.route.snapshot;

        this.truckNumber = snapshot.paramMap.get('truckNumber');

        this.computerType = snapshot.queryParamMap.get('computerType');
        this.deviceId = snapshot.queryParamMap.get('deviceId');
        this.ipAddress = snapshot.queryParamMap.get('ipAddress');
        this.location = snapshot.queryParamMap.get('location');
        this.site = snapshot.queryParamMap.get('site');
        this.softwareVersion = parseFloat(snapshot.queryParamMap.get('softwareVersion'));
        this.timestamp = moment.parseZone(snapshot.queryParamMap.get('timestamp'));
        this.business = Business[snapshot.queryParamMap.get('business')];

        this.cameras = this.cameraService.getCameras(this.computerType); // TODO: Error
        this.selectedCamerasMap = new Map<string, boolean>();
        this.cameras.forEach(camera => {
            this.selectedCamerasMap.set(camera, false);
        });

        for (let i = 1; i <= 12; i++) {
            this.hours.push(i.toString().padStart(2, '0'));
        }
        for (let i = 0; i <= 59; i++) {
            this.minutes.push(i.toString().padStart(2, '0'));
        }

        this.meridiems.push("AM");
        this.meridiems.push("PM");

        const requestDate = this.timestamp.clone().startOf('day');
        this.oldest = moment.utc().utcOffset(this.timestamp.utcOffset(), false).startOf('day').subtract(this.configService.config.allowedDaysInThePast, 'days');

        const half = Math.floor(configService.config.defaultVideoRequestLengthMinutes / 2.0);
        // from is half the default length before the time, but minimum of the start of the day
        let fromTime = this.timestamp.clone().subtract(half, 'minutes');
        if (fromTime.isBefore(requestDate)) {
            fromTime = requestDate.clone();
        }
        const toTime = fromTime.clone().add(configService.config.defaultVideoRequestLengthMinutes, 'minutes');

        // Put the YMD/HMS values into Date objects, for binding to UI controls.
        this.requestFromTime = this.dateService.momentPartsToBindableDate(fromTime);
        this.requestToTime = this.dateService.momentPartsToBindableDate(toTime);
        this.requestDate = this.dateService.momentPartsToBindableDate(requestDate);

        this.returnUrlService.save();
    }

    get businessCaption(): string {
        return Business[this.business];
    }

    get cost(): number {
        if (this.isFreeDownload) {
            return 0;
        }

        let selectedCamerasCount = 0;
        this.selectedCamerasMap.forEach((value: boolean, key: string) => {
            if (value) {
                selectedCamerasCount = selectedCamerasCount + 1;
            }
        });
        return this.userInfo.costPerCameraMinute
            ? this.userInfo.costPerCameraMinute * selectedCamerasCount * this.numberOfCameraMinutes
            : 0;
    }

    get minPickerDate(): Date {
        return this.dateService.momentPartsToBindableDate(this.oldest);
    }

    get maxPickerDate(): Date {
        const start = moment.utc().utcOffset(this.timestamp.utcOffset(), false).startOf('day');
        return this.dateService.momentPartsToBindableDate(start);
    }

    get offsetCaption(): string {
        return this.timestamp.format("Z");
    }

    get formattedDate(): string {
        return this._requestDate.toLocaleDateString('en-AU');
    }

    set formattedDate(date: string) {
        this._requestDate = moment(date).toDate();
    }

    get numberOfCameraMinutes(): number {
        const to = moment(this.requestToTime);
        const from = moment(this.requestFromTime);
        const diffMs = to.diff(from);
        const diffMins = Math.round(((diffMs % 86400000) % 3600000) / 60000); // minutes
        return diffMins;
    }

    get organisation(): string {
        return this.userInfo.councilName;
    }

    get requestDate(): Date {
        return this._requestDate;
    }

    set requestDate(date: Date) {
        this._requestDate = date;
    }

    get requestFromTime(): Date {
        this._requestFromTime = this.combine(this.requestDate,
            new Date(this.requestDate.getFullYear(), this.requestDate.getMonth(), this.requestDate.getDay(),
            this.convert12HoursTo24Hours(parseInt(this.selectedFromHour, 10), this.selectedFromMeridiem), parseInt(this.selectedFromMinute, 10)));
        return this._requestFromTime;
    }

    set requestFromTime(date: Date) {
        this._requestFromTime = date;
        this.selectedFromHour = this.convert24HoursTo12Hours(date.getHours()).toString().padStart(2, '0');
        this.selectedFromMinute = date.getMinutes().toString().padStart(2, '0');
        this.selectedFromMeridiem = this.getMeridiemFor24Hours(date.getHours());
    }

    get requestToTime(): Date {
        this._requestToTime = this.combine(this.requestDate,
            new Date(this.requestDate.getFullYear(), this.requestDate.getMonth(), this.requestDate.getDay(),
            this.convert12HoursTo24Hours(parseInt(this.selectedToHour, 10), this.selectedToMeridiem), parseInt(this.selectedToMinute, 10)));
        return this._requestToTime;
    }

    set requestToTime(date: Date) {
        this._requestToTime = date;
        this.selectedToHour = this.convert24HoursTo12Hours(date.getHours()).toString().padStart(2, '0');
        this.selectedToMinute = date.getMinutes().toString().padStart(2, '0');
        this.selectedToMeridiem = this.getMeridiemFor24Hours(date.getHours());
     }

    get selectedCameras(): string {
        const selectedCameras = [];
        this.selectedCamerasMap.forEach((value: boolean, key: string) => {
            if (value) {
                selectedCameras.push(key);
            }
        });
        return this.cameraService.transform(selectedCameras);
    }

    datePickerShouldBeDisabled(): boolean {
        const isUnitSearch = this.router.routerState.snapshot.root.queryParams['searchType'] === 'unit-search';
        const userIsInJJrRole = this.userInfo.isInRole('jjr');
        const enableDatePicker = userIsInJJrRole && isUnitSearch;

        return !enableDatePicker;
    }

    hasAtLeastOneSelectedCamera(): boolean {
        let hasASelectedCamera = false;
        this.selectedCamerasMap.forEach((value) => {
            if (value === true) {
                hasASelectedCamera = true;
            }
        });
        return hasASelectedCamera;
    }

    hasValidRequestDate(): boolean {
        // The date of the request must be between today (in the Site time zone),
        // and the oldest configured request date (inclusive).

        const todaySiteLocal = moment.utc().utcOffset(this.timestamp.utcOffset(), false).startOf('day');
        const requestDateSiteLocal = moment(this.requestDate).utcOffset(this.timestamp.utcOffset(), true);

        return (requestDateSiteLocal.isSameOrAfter(this.oldest) && requestDateSiteLocal.isSameOrBefore(todaySiteLocal));
    }

    getRequestLength(): number {
        const start = moment(this.combine(this.requestDate, this.requestFromTime));
        const end = moment(this.combine(this.requestDate, this.requestToTime));
        let duration = <Duration>null;
        if (start <= end) {
            duration = moment.duration(end.diff(start));
        } else {
            // Note: This handles the case where e.g. from = 11:59PM and to = 12:01AM.
            duration = moment.duration(end.add(1, 'days').diff(start));
        }
        // The length of the video request must be at least 1 minute,
        // and less than (or equal to) the maximum configured video request length.
        return duration.asMinutes();
    }

    hasValidRequestLength(): boolean {
        const minutes = this.getRequestLength();
        return minutes > 0 && minutes <= this.maxAllowedMinutes;
    }

    hasValidRequestLengthOrZero(): boolean {
        const minutes = this.getRequestLength();
        return minutes <= this.maxAllowedMinutes;
    }

    hasRequestLengthNonZero(): boolean {
        const minutes = this.getRequestLength();
        return minutes > 0;
    }

    hasValidReasonLength(): boolean {
        return (this.reason && this.reason.length > 1000) ? false : true;
    }

    get maxAllowedMinutes(): number {
        return (this.isFreeDownload || false)
            ? this.configService.config.allowedFreeDownloadVideoRequestLengthMinutes
            : this.configService.config.allowedVideoRequestLengthMinutes;
    }

    hasValidRequestTime(): boolean {
        // Now requestTimeEnd must be less than right now (i.e. no video requests in the future).
        // First we ned to build the "From" and "To" times by combining the request date with the appropriate time control.
        // Then, because truck requests are made in "Site Local Time" convert to UTC to ensure we can compare "now" with the
        // values to detect properly if they are in the past.

        const dateFromInput = this.combine(this.requestDate, this.requestFromTime);
        const dateToInput = this.combine(this.requestDate, this.requestToTime);
        const offset = this.timestamp.utcOffset();
        const dateFrom = this.dateService.bindableDatePartsToMoment(dateFromInput, offset);
        const dateTo = this.dateService.bindableDatePartsToMoment(dateToInput, offset);
        const nowUtc = moment().utc();
        return (dateFrom.isSameOrBefore(nowUtc) && dateTo.isSameOrBefore(nowUtc));
    }

    isValid(): boolean {
        return !!(
            this.title &&
            this.requestFromTime &&
            this.requestToTime &&
            this.requestDate &&
            this.hasAtLeastOneSelectedCamera() &&
            this.hasValidRequestDate() &&
            this.hasValidRequestLength() &&
            this.hasValidRequestTime() &&
            this.hasValidReasonLength());
    }

    onFreeDownloadChanged(value: boolean): void {
        if (value) {
            if (this.userInfo.isFreeUser) {
                $(this.freeDownloadDisclaimerModal.nativeElement).modal('show');
            } else {
                this.isFreeDownload = false;
            }
        }
    }

    onCameraSelected(camera: string, value: boolean): void {
        this.selectedCamerasMap.set(camera, value);
    }

    rejectFreeDownload(): void {
        this.isFreeDownload = false;
    }

    requestVideo(): void {
        this.flashMessagesService.clear(this.messageIds.httpError);
        // requestDate, requestFromTime, requestToTime, are all JS Date objects for binding to the UI
        // they are all local values for the browser,
        // but with the input values interpreted as local for the site
        // e.g. input '15:12' in an Brisbane browser is a Date object for +10 (so is actually 05:12+0),
        // but we want it interpreted as local to the site, e.g. Auckland, as 15:12+13.
        const dateFromInput = this.combine(this.requestDate, this.requestFromTime);
        const dateToInput = this.combine(this.requestDate, this.requestToTime);
        const offset = this.timestamp.utcOffset();
        const dateFrom = this.dateService.bindableDatePartsToMoment(dateFromInput, offset);
        const dateTo = this.dateService.bindableDatePartsToMoment(dateToInput, offset);

        const videoRequest = {
            cameras: this.selectedCameras,
            computerType: this.computerType,
            deviceId: this.deviceId,
            dateFrom: dateFrom.format(),
            dateTo: dateTo.format(),
            ipAddress: this.ipAddress,
            location: this.location,
            reason: this.reason,
            site: this.site,
            softwareVersion: this.softwareVersion,
            title: this.title,
            truckNumber: this.truckNumber,
            isFreeDownload: this.isFreeDownload,
            business: this.business,
        } as CreateVideoRequest;

        this.videoRequestService.put(videoRequest).subscribe(vrrId => {
            // noinspection JSIgnoredPromiseFromCall
            this.router.navigate(['video-requests', vrrId]);
        }, error => {
            this.messageIds.httpError = this.flashMessagesService.addMessageFor(error);
        });
    }

    // noinspection JSMethodCanBeStatic
    private combine(date: Date, time: Date): Date {
        return new Date(date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            time.getHours(),
            time.getMinutes(),
            time.getSeconds(),
            time.getMilliseconds());
    }

    private convert12HoursTo24Hours(hours: number, meridiem: string): number {
        if (meridiem.toUpperCase() === "AM") {
            if (hours === 12) {
                return 0;
            }
            return hours;
        }
        if (meridiem.toUpperCase() === "PM") {
            if (hours === 12) {
                return hours;
            }
            return hours + 12;
        }
        return hours;
    }

    private convert24HoursTo12Hours(hours: number): number {
        if (hours === 0) {
            return 12;
        }
        if (hours <= 12) {
            return hours;
        }
        return hours - 12;
    }

    private getMeridiemFor24Hours(hours: number): string {
        if (hours <= 11) {
            return "AM";
        }
        return "PM";
    }
}
